import React, { useState } from 'react';
import { Check, X, ArrowRight, Layers, Clock, BookOpen, TrendingUp, Shield, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import { CampaignSelector } from '../components/CampaignSelector';
const fmt = (n: number) => n.toLocaleString('en-US');
export function EdgeProduct() {
  const [campaignMode, setCampaignMode] = useState(1); // 0 = partial, 1 = single, 2+ = multiple
  const isPartial = campaignMode === 0;
  const campaigns = isPartial ? 1 : campaignMode;
  const buildFee = isPartial ? 5000 : 5000 + 1000 * (campaignMode - 1);
  const systemFee = 1500;
  const campaignFee = isPartial ? 750 : 1500 * campaigns;
  const monthlyTotal = systemFee + campaignFee;
  return <div className="min-h-screen w-full bg-[#0A0F1C] text-slate-200 selection:bg-[#7FE0C3]/30 font-sans" data-id="element-789">
      {/* 1. HERO */}
      <section className="relative pt-32 pb-20 px-6 md:px-12 max-w-7xl mx-auto text-center" data-id="element-790">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-96 bg-slate-500/5 blur-[120px] rounded-full pointer-events-none" data-id="element-791" />

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-792">
          <div className="inline-block px-3 py-1 rounded-full bg-slate-800 border border-slate-700 text-slate-400 text-xs font-bold uppercase tracking-widest mb-6" data-id="element-793">
            Recruiting Infrastructure
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight" data-id="element-794">
            HIRE Edge
          </h1>
          <p className="text-xl md:text-2xl text-slate-400 max-w-3xl mx-auto mb-8 leading-relaxed" data-id="element-795">
            Predictable applicant flow — without changing how you run your team.
          </p>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto border-t border-slate-800 pt-8" data-id="element-796">
            HIRE Edge is designed for teams that want better candidates while
            keeping hiring and onboarding internal.
          </p>
        </motion.div>
      </section>

      {/* 2. WHAT EDGE IS (AND ISN'T) */}
      <section className="py-20 px-6 md:px-12 max-w-5xl mx-auto border-t border-slate-800/50" data-id="element-797">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-798">
          <div data-id="element-799">
            <h3 className="text-lg font-bold text-white mb-6 flex items-center" data-id="element-800">
              <Check className="w-5 h-5 text-[#7FE0C3] mr-2" data-id="element-801" />
              HIRE Edge is:
            </h3>
            <ul className="space-y-4" data-id="element-802">
              {['A recruiting infrastructure layer', 'A predictable source of pre-screened applicants', 'A clean, managed top-of-funnel system'].map((item, idx) => <li key={idx} className="text-slate-300 flex items-start" data-id="element-803">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#7FE0C3] mr-3 mt-2" data-id="element-804" />
                  {item}
                </li>)}
            </ul>
          </div>
          <div data-id="element-805">
            <h3 className="text-lg font-bold text-white mb-6 flex items-center" data-id="element-806">
              <X className="w-5 h-5 text-slate-500 mr-2" data-id="element-807" />
              HIRE Edge is not:
            </h3>
            <ul className="space-y-4" data-id="element-808">
              {['A hiring or onboarding solution', 'A training or retention program', 'A replacement for internal leadership'].map((item, idx) => <li key={idx} className="text-slate-400 flex items-start" data-id="element-809">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-600 mr-3 mt-2" data-id="element-810" />
                  {item}
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* 3. OWNERSHIP MAP */}
      <section className="py-20 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800" data-id="element-811">
        <div className="max-w-6xl mx-auto" data-id="element-812">
          <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-813">
            Ownership Map
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16" data-id="element-814">
            {/* HIRE Owns */}
            <div className="bg-slate-900/40 border border-slate-700 rounded-xl p-8 relative overflow-hidden" data-id="element-815">
              <div className="absolute top-0 left-0 w-full h-1 bg-slate-600" data-id="element-816" />
              <h3 className="text-xl font-bold text-white mb-8 flex items-center" data-id="element-817">
                <span className="w-2 h-2 rounded-full bg-slate-600 mr-3" data-id="element-818" />
                HIRE Owns
              </h3>
              <ul className="space-y-4" data-id="element-819">
                {['Job boards and recruiting traffic', 'Screening and pre-qualification', 'Multi-market coordination', 'Reporting and visibility'].map((item, idx) => <li key={idx} className="flex items-start text-slate-300" data-id="element-820">
                    <Check className="w-5 h-5 text-slate-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-821" />
                    <span data-id="element-822">{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* You Own */}
            <div className="bg-slate-900/40 border border-slate-700 rounded-xl p-8 relative overflow-hidden" data-id="element-823">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#7FE0C3]" data-id="element-824" />
              <h3 className="text-xl font-bold text-white mb-8 flex items-center" data-id="element-825">
                <span className="w-2 h-2 rounded-full bg-[#7FE0C3] mr-3" data-id="element-826" />
                You Own
              </h3>
              <ul className="space-y-4" data-id="element-827">
                {['Interviews and hiring decisions', 'Onboarding and training', 'Retention and performance'].map((item, idx) => <li key={idx} className="flex items-start text-slate-300" data-id="element-828">
                    <Check className="w-5 h-5 text-[#7FE0C3] mr-3 mt-0.5 flex-shrink-0" data-id="element-829" />
                    <span data-id="element-830">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>

          <div className="mt-8 text-center" data-id="element-831">
            <p className="text-slate-400" data-id="element-832">
              Edge works best when your internal team already knows how to hire
              and onboard effectively.
            </p>
          </div>
        </div>
      </section>

      {/* 4. HOW EDGE FITS INTO THE SYSTEM */}
      <section className="py-20 px-6 md:px-12 max-w-4xl mx-auto" data-id="element-833">
        <div className="bg-slate-900/30 border border-slate-800 rounded-xl p-8 md:p-12 text-center" data-id="element-834">
          <h2 className="text-2xl font-bold text-white mb-6" data-id="element-835">
            How Edge Fits Into The System
          </h2>
          <p className="text-slate-300 mb-6 leading-relaxed" data-id="element-836">
            All HIRE partners use the same 10-step recruiting system. Edge
            focuses on the infrastructure stages so your team can handle
            execution.
          </p>
          <div className="flex flex-wrap justify-center gap-3 text-sm" data-id="element-837">
            {['Attract', 'Engage', 'Screen', 'Qualify'].map((step, idx) => <span key={idx} className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-full text-slate-400" data-id="element-838">
                {step}
              </span>)}
            <span className="px-4 py-2 text-slate-600" data-id="element-839">→</span>
            {['Interview', 'Commit', 'Onboard', 'Integrate', 'Train', 'Launch', 'Retain'].map((step, idx) => <span key={idx} className="px-4 py-2 bg-slate-900 border border-slate-800 rounded-full text-slate-500" data-id="element-840">
                {step}
              </span>)}
          </div>
          <p className="text-xs text-slate-500 mt-6" data-id="element-841">
            Infrastructure stages (highlighted) vs. execution stages
          </p>
        </div>
      </section>

      {/* 5. PRICING */}
      <section className="py-20 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800" data-id="element-842">
        <div className="max-w-6xl mx-auto" data-id="element-843">
          <div className="text-center mb-12" data-id="element-844">
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-845">
              Partnership Pricing
            </h2>
            <p className="text-slate-400" data-id="element-846">
              Predictable applicant flow — without changing how you run your
              team.
            </p>
          </div>

          <CampaignSelector mode={campaignMode} onChange={setCampaignMode} accent="slate" data-id="element-847" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-848">
            {/* Build Fee */}
            <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-xl relative overflow-hidden" data-id="element-849">
              <div className="mb-4" data-id="element-850">
                <div className="text-xs text-slate-500 uppercase tracking-widest mb-2" data-id="element-851">
                  Build and Implementation (One-Time)
                </div>
                <div className="text-3xl font-bold text-white" data-id="element-852">
                  ${fmt(buildFee)}
                </div>
              </div>
            </div>

            {/* Monthly Total */}
            <div className="bg-slate-900/40 border border-slate-600 p-6 rounded-xl relative" data-id="element-853">
              <div className="absolute top-0 left-0 w-full h-1 bg-slate-500 rounded-t-xl" data-id="element-854" />
              <div className="mb-4" data-id="element-855">
                <div className="text-xs text-slate-400 uppercase tracking-widest mb-2" data-id="element-856">
                  Total Monthly
                </div>
                <div className="text-4xl font-bold text-white mb-1" data-id="element-857">
                  ${fmt(monthlyTotal)}
                </div>
                <div className="text-sm text-slate-500 mb-6" data-id="element-858">/ month</div>

                <div className="space-y-3 pt-4 border-t border-slate-800/50" data-id="element-859">
                  <div className="flex justify-between text-sm" data-id="element-860">
                    <span className="text-slate-400" data-id="element-861">System Fee</span>
                    <span className="text-white font-medium" data-id="element-862">
                      ${fmt(systemFee)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-863">
                    <span className="text-slate-400" data-id="element-864">
                      {isPartial ? 'Partial Campaign' : campaigns === 1 ? 'Full Campaign' : `Full Campaigns (×${campaigns})`}
                    </span>
                    <span className="text-white font-medium" data-id="element-865">
                      ${fmt(campaignFee)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Performance Bonuses */}
            <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-xl" data-id="element-866">
              <div className="mb-4" data-id="element-867">
                <div className="text-xs text-amber-400 uppercase tracking-widest mb-4" data-id="element-868">
                  Performance Bonuses
                </div>
                <ul className="space-y-3" data-id="element-869">
                  <li className="flex items-start" data-id="element-870">
                    <span className="text-amber-400 mr-3 text-xl font-bold" data-id="element-871">
                      $250
                    </span>
                    <span className="text-slate-300 text-lg" data-id="element-872">
                      per rep at launch
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-873">
                    <span className="text-amber-400 mr-3 text-xl font-bold" data-id="element-874">
                      $250
                    </span>
                    <span className="text-slate-300 text-lg" data-id="element-875">
                      per rep at 30 days
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-876">
                    <span className="text-amber-400 mr-3 text-xl font-bold" data-id="element-877">
                      $250
                    </span>
                    <span className="text-slate-300 text-lg" data-id="element-878">
                      per rep at 60 days
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-879">
                    <span className="text-amber-400 mr-3 text-xl font-bold" data-id="element-880">
                      $250
                    </span>
                    <span className="text-slate-300 text-lg" data-id="element-881">
                      per rep at 90 days
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-8 p-6 bg-[#7FE0C3]/5 border border-[#7FE0C3]/20 rounded-lg max-w-3xl mx-auto" data-id="element-882">
            <p className="text-[#7FE0C3] text-center font-medium" data-id="element-883">
              Ad spend is paid directly to the job platforms and is separate
              from HIRE's fees.
            </p>
          </div>
        </div>
      </section>

      {/* 6. LMS + SUPPORT */}
      <section className="py-20 px-6 md:px-12 max-w-4xl mx-auto" data-id="element-884">
        <div className="flex items-start space-x-6 bg-slate-900/30 border border-slate-800 rounded-xl p-8" data-id="element-885">
          <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0" data-id="element-886">
            <BookOpen className="w-6 h-6 text-slate-400" data-id="element-887" />
          </div>
          <div data-id="element-888">
            <h3 className="text-xl font-bold text-white mb-4" data-id="element-889">
              Learning Management System Access
            </h3>
            <p className="text-slate-300 mb-4 leading-relaxed" data-id="element-890">
              All Edge partners receive access to HIRE's Learning Management
              System covering recruiting and hiring best practices.
            </p>
            <ul className="space-y-2 text-slate-400 text-sm" data-id="element-891">
              <li className="flex items-start" data-id="element-892">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-600 mr-2 mt-1.5" data-id="element-893" />
                DIY reference library
              </li>
              <li className="flex items-start" data-id="element-894">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-600 mr-2 mt-1.5" data-id="element-895" />
                Optional support and guidance
              </li>
              <li className="flex items-start" data-id="element-896">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-600 mr-2 mt-1.5" data-id="element-897" />
                Not a replacement for internal execution
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* 7. WHO EDGE IS FOR */}
      <section className="py-20 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800" data-id="element-898">
        <div className="max-w-5xl mx-auto" data-id="element-899">
          <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-900">
            Who Edge Is For
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-901">
            <div data-id="element-902">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center" data-id="element-903">
                <Check className="w-5 h-5 text-[#7FE0C3] mr-2" data-id="element-904" />
                Edge is a good fit if you:
              </h3>
              <ul className="space-y-4" data-id="element-905">
                {['Already have strong onboarding and training', 'Want better candidate quality and predictability', 'Prefer to keep hiring decisions internal', 'Want a lower-commitment entry point'].map((item, idx) => <li key={idx} className="text-slate-400 flex items-start" data-id="element-906">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#7FE0C3] mr-3 mt-2" data-id="element-907" />
                    {item}
                  </li>)}
              </ul>
            </div>
            <div data-id="element-908">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center" data-id="element-909">
                <X className="w-5 h-5 text-slate-500 mr-2" data-id="element-910" />
                Edge is not a good fit if you:
              </h3>
              <ul className="space-y-4" data-id="element-911">
                {['Want help fixing retention', 'Want hiring standards designed for you', 'Want recruiting execution handled externally'].map((item, idx) => <li key={idx} className="text-slate-400 flex items-start" data-id="element-912">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-600 mr-3 mt-2" data-id="element-913" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 8. UPGRADE PATH */}
      <section className="py-20 px-6 md:px-12 max-w-4xl mx-auto" data-id="element-914">
        <div className="flex items-start space-x-6 bg-slate-900/30 border border-slate-800 rounded-xl p-8" data-id="element-915">
          <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0" data-id="element-916">
            <TrendingUp className="w-6 h-6 text-slate-400" data-id="element-917" />
          </div>
          <div data-id="element-918">
            <h3 className="text-xl font-bold text-white mb-4" data-id="element-919">
              Natural Progression
            </h3>
            <p className="text-slate-300 leading-relaxed" data-id="element-920">
              Many Edge partners later move into Advantage when they want system
              ownership and accountability — without changing platforms or
              starting over.
            </p>
          </div>
        </div>
      </section>

      {/* 9. CTA */}
      <section className="py-32 px-6 md:px-12 text-center border-t border-slate-800/50" data-id="element-921">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-8" data-id="element-922">
          See if HIRE Edge fits your hiring model.
        </h2>
        <button className="inline-flex items-center px-8 py-4 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-lg transition-all" data-id="element-923">
          Learn More
          <ArrowRight className="ml-2 w-5 h-5" data-id="element-924" />
        </button>
      </section>
    </div>;
}